import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

export default function MediaCoverage() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/media-coverage');
  }, []);

  const { getValue, getHtml, loading: cmsLoading } = useCMS();

  // 市場熱話（服務器記憶體市場新聞；每日排程更新）
  interface NewsItem { id: number; news_date: string; title: string; summary: string; source_name: string; source_url: string; image_url?: string }
  const [news, setNews] = useState<NewsItem[]>([]);
  useEffect(() => {
    fetch('/api/public/media-news')
      .then((r) => r.json())
      .then((j) => { if (j.success && Array.isArray(j.data)) setNews(j.data); })
      .catch(() => {});
  }, []);

  const mediaImages = [
    {
      src: getValue('story.media.image1', ''),
      caption: getValue('story.media.caption1', ''),
    },
    {
      src: getValue('story.media.image2', ''),
      caption: getValue('story.media.caption2', ''),
    },
    {
      src: getValue('story.media.image3', ''),
      caption: getValue('story.media.caption3', ''),
    },
    {
      src: getValue('story.media.image4', ''),
      caption: getValue('story.media.caption4', ''),
    },
    {
      src: getValue('story.media.image5', ''),
      caption: getValue('story.media.caption5', ''),
    },
    {
      src: getValue('story.media.image6', ''),
      caption: getValue('story.media.caption6', ''),
    },
    {
      src: getValue('story.media.image7', ''),
      caption: getValue('story.media.caption7', ''),
    },
    {
      src: getValue('story.media.image8', ''),
      caption: getValue('story.media.caption8', ''),
    },
    {
      src: getValue('story.media.image9', ''),
      caption: getValue('story.media.caption9', ''),
    },
    {
      src: getValue('story.media.image10', ''),
      caption: getValue('story.media.caption10', ''),
    },
    {
      src: getValue('story.media.image11', ''),
      caption: getValue('story.media.caption11', ''),
    },
    {
      src: getValue('story.media.image12', ''),
      caption: getValue('story.media.caption12', ''),
    },
    {
      src: getValue('story.media.image13', ''),
      caption: getValue('story.media.caption13', ''),
    },
    {
      src: getValue('story.media.image14', ''),
      caption: getValue('story.media.caption14', ''),
    },
    {
      src: getValue('story.media.image15', ''),
      caption: getValue('story.media.caption15', ''),
    },
    {
      src: getValue('story.media.image16', ''),
      caption: getValue('story.media.caption16', ''),
    },
    {
      src: getValue('story.media.image17', ''),
      caption: getValue('story.media.caption17', ''),
    },
    {
      src: getValue('story.media.image18', ''),
      caption: getValue('story.media.caption18', ''),
    },
    {
      src: getValue('story.media.image19', ''),
      caption: getValue('story.media.caption19', ''),
    },
  ];

  // Kare：淨係顯示後台設定咗圖嘅格（story.media.imageN 空＝跳過）
  const visibleImages = mediaImages.filter((i) => i.src);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-20 md:py-28 overflow-hidden">
        {getValue('media.hero.image', '') && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${getValue('media.hero.image', '')}')` }}
          />
        )}
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-x1493-secondary font-bold tracking-widest mb-4">媒體報道</p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            dangerouslySetInnerHTML={getHtml('mediaCoverage.hero.title', 'Kare Tech<br />媒體報導')}
          />
          <p
            className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto"
            dangerouslySetInnerHTML={getHtml('mediaCoverage.hero.subtitle', 'Kare Tech 的報導與花絮，一齊睇下媒體點講。')}
          />
        </div>
      </section>

      {/* 最新報導（原「市場熱話」——2026-10-20 改名：內容已擴至 AI/手機/Intel 等，舊名誤導老闆搵唔到） */}
      {news.length > 0 && (
        <section className='py-12 bg-stone-50'>
          <div className='max-w-6xl mx-auto px-4'>
            <div className='flex items-center gap-3 mb-6'>
              <span className='inline-flex items-center gap-1.5 bg-x1493-primary/10 text-x1493-primary text-sm font-bold px-3 py-1.5 rounded-full'>
                🔥 最新報導
              </span>
              <span className='text-xs text-stone-400'>每日更新</span>
            </div>
            <div className='space-y-4'>
              {news.map((item) => (
                <article key={item.id} className='bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden flex flex-col sm:flex-row'>
                  {item.image_url && (
                    <div className='sm:w-48 shrink-0 bg-stone-100'>
                      <img src={item.image_url} alt={item.title} className='w-full h-44 sm:h-full object-cover' loading='lazy' />
                    </div>
                  )}
                  <div className='p-5 md:p-6 flex-1'>
                  <div className='flex items-center gap-2 mb-2 text-xs text-stone-400'>
                    <span className='font-bold text-stone-500'>{item.news_date}</span>
                    {item.source_name && <span>・{item.source_name}</span>}
                  </div>
                  <h3 className='text-lg md:text-xl font-bold mb-2 text-stone-800'>{item.title}</h3>
                  <p className='text-stone-600 text-sm leading-relaxed mb-3'>{item.summary}</p>
                  <Link to={`/media-coverage/${item.id}`} className='text-x1493-primary text-sm font-bold hover:underline'>
                    詳情 →
                  </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Media Coverage Masonry */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('mediaCoverage.media.title', '媒體報道')}
            />
            <p
              className="text-stone-600 max-w-2xl mx-auto"
              dangerouslySetInnerHTML={getHtml('mediaCoverage.media.description', 'Kare Tech 的故事，等媒體朋友一齊記錄。')}
            />
          </div>

          {visibleImages.length === 0 ? (
            <div className="text-center py-16 bg-stone-50 rounded-2xl">
              <p className="text-stone-500">媒體報導整理中——有新報導會第一时间放上嚟。</p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
            {visibleImages.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setLightboxImage(item.src)}
                className="block w-full break-inside-avoid rounded-2xl overflow-hidden group relative text-left"
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  className="w-full h-auto object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                    點擊放大
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition duration-300">
                  <p className="text-white font-medium text-sm leading-snug">
                    {item.caption}
                  </p>
                </div>
              </button>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            aria-label="關閉"
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt="放大預覽"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
